import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { supabase } from "@/lib/supabase";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

/* ---------------------------------------------------------------------------
 * Auth architecture
 *
 * There is exactly ONE subscription to `supabase.auth.onAuthStateChange` in
 * the whole app — this one. Everything else routes on the state exposed here.
 *
 * State machine:
 *
 *   "initializing"  → we have not yet received INITIAL_SESSION from
 *                     supabase-js. All routes wait.
 *   "anonymous"     → INITIAL_SESSION fired with no session. Protected
 *                     routes redirect to /login; /login and /auth/callback
 *                     render normally.
 *   "authenticated" → we have a Supabase session AND a hydrated app profile
 *                     from /api/auth/me. `user` is populated.
 *
 * Access token is cached in module scope so the axios interceptor is
 * synchronous (no getSession() calls — that's where the lock deadlock lives).
 * The cache is kept in sync via `onAuthStateChange` events; supabase-js also
 * fires TOKEN_REFRESHED, so it stays fresh across auto-refreshes.
 * -------------------------------------------------------------------------*/

let accessToken = null;
export const getAccessToken = () => accessToken;

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [status, setStatus] = useState("initializing");
  const [user, setUser] = useState(null);

  const hydrateProfile = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      return res.data;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    // ONE subscription for the whole app. supabase-js guarantees at least one
    // event (INITIAL_SESSION) after the client's internal init completes.
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      if (!session) {
        accessToken = null;
        setUser(null);
        setStatus("anonymous");
        return;
      }
      accessToken = session.access_token;
      const profile = await hydrateProfile();
      if (!profile) {
        // JWT valid but backend profile fetch failed — treat as anonymous so
        // the user can retry sign-in. Do NOT keep them stuck on initializing.
        accessToken = null;
        setUser(null);
        setStatus("anonymous");
        return;
      }
      setUser(profile);
      setStatus("authenticated");
    });

    return () => {
      sub?.subscription?.unsubscribe?.();
    };
  }, [hydrateProfile]);

  const logout = useCallback(async () => {
    await supabase.auth.signOut(); // triggers onAuthStateChange -> anonymous
  }, []);

  const refresh = useCallback(async () => {
    const profile = await hydrateProfile();
    if (profile) setUser(profile);
    return profile;
  }, [hydrateProfile]);

  // Presence heartbeat — while a signed-in user has any tab in the
  // foreground, we POST /presence/heartbeat every 20 s so their friends see
  // the green "online" dot. We also fire immediately on authentication and
  // when the tab regains focus so the status is near-realtime without
  // needing a websocket.
  useEffect(() => {
    if (status !== "authenticated") return;
    let alive = true;

    const beat = async () => {
      if (!alive || document.visibilityState !== "visible") return;
      try {
        await api.post("/presence/heartbeat");
      } catch {
        /* transient — the next tick will retry */
      }
    };

    beat();
    const iv = setInterval(beat, 20_000);
    const onVis = () => {
      if (document.visibilityState === "visible") beat();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [status]);

  const value = useMemo(
    () => ({
      status,
      user,
      // Kept for backwards compatibility with existing screens that read these:
      loading: status === "initializing",
      setUser,           // used by Profile after mutation
      refresh,           // re-fetch /api/auth/me
      logout,
    }),
    [status, user, refresh, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
